
import React from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { generatePreviewId, processJsonForPreview, storePreviewData } from '@/utils/previewUtils';

interface PreviewButtonProps {
  jsonContent: string;
  solutionName?: string;
  solutionShortDescription?: string;
  solutionCategory?: string;
  solutionTags?: string[];
  imageUrl?: string;
  className?: string;
  text?: string;
}

const PreviewButton = ({
  jsonContent,
  solutionName,
  solutionShortDescription,
  solutionCategory,
  solutionTags,
  imageUrl,
  className,
  text = "Preview Page"
}: PreviewButtonProps) => {
  const handlePreviewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!jsonContent || !jsonContent.trim()) {
      toast({
        title: "No content to preview",
        description: "Please add some JSON content before previewing.",
        variant: "destructive"
      });
      return;
    }

    const previewId = generatePreviewId();
    const processedData = processJsonForPreview(
      jsonContent, 
      solutionName, 
      solutionShortDescription, 
      solutionCategory, 
      solutionTags,
      imageUrl
    );
    
    if (processedData) {
      storePreviewData(previewId, processedData);
      const previewUrl = `/solution/preview/${previewId}`;
      const newWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');
      
      if (newWindow) {
        toast({
          title: "Preview opened",
          description: "Your solution preview has opened in a new tab."
        });
      } else {
        toast({
          title: "Preview blocked",
          description: "Please allow popups for this site and try again.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Invalid JSON format",
        description: "Please check your JSON content for syntax errors.",
        variant: "destructive"
      });
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handlePreviewClick}
      className={className}
    >
      <ExternalLink className="w-4 h-4 mr-2" />
      {text}
    </Button>
  );
};

export default PreviewButton;
